/**
 * OfflineContentManager - Manages downloadable content packs for offline learning
 */

export class OfflineContentManager {
  constructor() {
    this.contentPacks = new Map();
    this.downloadQueue = [];
    this.storage = {
      quota: 0,
      usage: 0,
      available: 0
    };
    this.isOnline = navigator.onLine;
  }

  /**
   * Initialize offline content system
   */
  async initializeOfflineContent() {
    // Check storage quota
    await this.checkStorageQuota();

    // Register available content packs
    await this.registerContentPacks();

    // Set up online/offline event listeners
    this.setupNetworkListeners();

    // Load cached content info
    await this.loadCachedContentInfo();
  }

  /**
   * Register available content packs
   */
  async registerContentPacks() {
    if (this.contentPacks.size > 0) return;

    const remotePacks = await this.fetchContentPackIndex();
    if (remotePacks?.length) {
      remotePacks.forEach((pack) => {
        this.contentPacks.set(pack.id, {
          ...pack,
          content: pack.content || {}
        });
      });
      return;
    }

    // Biome-specific content packs
    this.contentPacks.set('tropical_rainforest', {
      id: 'tropical_rainforest',
      name: 'Tropical Rainforest Explorer',
      description: 'Complete guide to tropical rainforests with interactive content',
      size: 2.5, // MB
      version: '1.0.0',
      includes: ['images', 'quiz_questions', 'educational_content', 'sound_clips'],
      offline_priority: 'high',
      educational_level: ['elementary', 'middle_school'],
      subjects: ['biology', 'environmental_science'],
      content: {
        images: 15,
        quiz_questions: 25,
        articles: 8,
        sound_clips: 10
      }
    });

    this.contentPacks.set('desert', {
      id: 'desert',
      name: 'Desert Survival Guide',
      description: 'Explore desert ecosystems and survival adaptations',
      size: 1.8,
      version: '1.0.0',
      includes: ['images', 'quiz_questions', 'educational_content'],
      offline_priority: 'high',
      educational_level: ['elementary', 'middle_school', 'high_school'],
      subjects: ['biology', 'geography', 'environmental_science'],
      content: {
        images: 12,
        quiz_questions: 20,
        articles: 6,
        sound_clips: 0
      }
    });

    this.contentPacks.set('arctic_tundra', {
      id: 'arctic_tundra',
      name: 'Arctic Tundra Adventure',
      description: 'Journey through the frozen wilderness of the Arctic',
      size: 3.1,
      version: '1.0.0',
      includes: ['images', 'quiz_questions', 'educational_content', 'videos'],
      offline_priority: 'medium',
      educational_level: ['middle_school', 'high_school'],
      subjects: ['biology', 'climate_science', 'geography'],
      content: {
        images: 18,
        quiz_questions: 30,
        articles: 10,
        videos: 3
      }
    });

    this.contentPacks.set('coral_reef', {
      id: 'coral_reef',
      name: 'Coral Reef Conservation',
      description: 'Discover marine biodiversity and conservation efforts',
      size: 4.2,
      version: '1.0.0',
      includes: ['images', 'quiz_questions', 'educational_content', 'videos', 'sound_clips'],
      offline_priority: 'high',
      educational_level: ['elementary', 'middle_school', 'high_school'],
      subjects: ['marine_biology', 'environmental_science', 'conservation'],
      content: {
        images: 25,
        quiz_questions: 35,
        articles: 12,
        videos: 5,
        sound_clips: 8
      }
    });

    this.contentPacks.set('temperate_forest', {
      id: 'temperate_forest',
      name: 'Temperate Forest Seasons',
      description: 'Experience the changing seasons in temperate forests',
      size: 2.9,
      version: '1.0.0',
      includes: ['images', 'quiz_questions', 'educational_content', 'sound_clips'],
      offline_priority: 'medium',
      educational_level: ['elementary', 'middle_school'],
      subjects: ['biology', 'ecology', 'seasonal_changes'],
      content: {
        images: 20,
        quiz_questions: 28,
        articles: 9,
        sound_clips: 12
      }
    });

    // Essential content pack (always available)
    this.contentPacks.set('essential', {
      id: 'essential',
      name: 'Essential Biome Basics',
      description: 'Core educational content available offline',
      size: 1.2,
      version: '1.0.0',
      includes: ['quiz_questions', 'educational_content'],
      offline_priority: 'critical',
      educational_level: ['elementary', 'middle_school', 'high_school'],
      subjects: ['geography', 'biology'],
      content: {
        images: 8,
        quiz_questions: 40,
        articles: 15,
        sound_clips: 0
      },
      auto_download: true
    });
  }

  async fetchContentPackIndex() {
    try {
      const response = await fetch('/data/content-packs/index.json', { cache: 'no-cache' });
      if (!response.ok) {
        return null;
      }
      const data = await response.json();
      if (!data?.packs) {
        return null;
      }

      return data.packs.map(pack => ({
        ...pack,
        content: pack.content || {}
      }));
    } catch (error) {
      console.warn('Failed to load remote content pack index:', error);
      return null;
    }
  }

  /**
   * Check storage quota and usage
   */
  async checkStorageQuota() {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      try {
        const estimate = await navigator.storage.estimate();
        this.storage = {
          quota: Math.round((estimate.quota || 0) / (1024 * 1024)), // MB
          usage: Math.round((estimate.usage || 0) / (1024 * 1024)), // MB
          available: Math.round(((estimate.quota || 0) - (estimate.usage || 0)) / (1024 * 1024)) // MB
        };
      } catch (error) {
        console.warn('Could not estimate storage quota:', error);
        // Fallback values
        this.storage = {
          quota: 100, // Assume 100MB quota
          usage: 0,
          available: 100
        };
      }
    }
  }

  /**
   * Get available content packs
   */
  getAvailableContentPacks(filters = {}) {
    let packs = Array.from(this.contentPacks.values());

    // Filter by educational level
    if (filters.educational_level) {
      packs = packs.filter(pack =>
        pack.educational_level.includes(filters.educational_level)
      );
    }

    // Filter by subject
    if (filters.subject) {
      packs = packs.filter(pack =>
        pack.subjects.includes(filters.subject)
      );
    }

    // Filter by size limit
    if (filters.max_size) {
      packs = packs.filter(pack => pack.size <= filters.max_size);
    }

    // Sort by priority and size
    return packs.sort((a, b) => {
      const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
      const aPriority = priorityOrder[a.offline_priority] || 3;
      const bPriority = priorityOrder[b.offline_priority] || 3;

      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }
      return a.size - b.size; // Smaller packs first
    });
  }

  /**
   * Download content pack
   */
  async downloadContentPack(packId, options = {}) {
    const pack = this.contentPacks.get(packId);
    if (!pack) {
      throw new Error(`Content pack '${packId}' not found`);
    }

    // Check if already downloaded
    const cached = await this.getCachedContent(packId);
    if (cached && cached.version === pack.version && !options.force) {
      return { status: 'already_downloaded', pack };
    }

    // Check storage space
    if (pack.size > this.storage.available) {
      throw new Error(`Insufficient storage space. Need ${pack.size}MB, have ${this.storage.available}MB`);
    }

    // Check network status
    if (!this.isOnline) {
      throw new Error('Network connection required to download content');
    }

    try {
      // Add to download queue
      this.downloadQueue.push(packId);
      this.onDownloadStarted?.(packId, pack);

      const content = await this.fetchContentPack(packId);

      // Cache the content
      const cachedData = await this.cacheContent(packId, content, pack.version);

      // Remove from queue
      this.downloadQueue = this.downloadQueue.filter(id => id !== packId);
      this.onDownloadCompleted?.(packId, pack);

      // Update storage usage
      await this.checkStorageQuota();

      // Update pack metadata
      pack.cached = true;
      pack.cached_at = cachedData.cached_at;
      pack.cached_version = cachedData.version;
      pack.cached_size = cachedData.size;

      return { status: 'downloaded', pack, content };

    } catch (error) {
      this.downloadQueue = this.downloadQueue.filter(id => id !== packId);
      this.onDownloadError?.(packId, pack, error);
      throw error;
    }
  }

  /**
   * Fetch content pack data (simulated)
   */
  async fetchContentPack(packId) {
    const pack = this.contentPacks.get(packId);

    try {
      const response = await fetch(`/data/content-packs/${packId}.json`, { cache: 'no-cache' });
      if (response.ok) {
        const payload = await response.json();
        return {
          metadata: {
            id: packId,
            downloaded_at: new Date().toISOString(),
            version: pack.version,
            size: payload.metadata?.size || pack.size
          },
          images: payload.images || [],
          quiz_questions: payload.quiz_questions || [],
          articles: payload.articles || [],
          sound_clips: payload.sound_clips || [],
          videos: payload.videos || []
        };
      }
    } catch (error) {
      console.warn(`Failed to fetch content pack '${packId}' from CDN:`, error);
    }

    // Fallback to simulated content if remote assets are unavailable
    const content = {
      metadata: {
        id: packId,
        downloaded_at: new Date().toISOString(),
        version: pack.version,
        size: pack.size
      },
      images: [],
      quiz_questions: [],
      articles: [],
      sound_clips: [],
      videos: []
    };

    for (let i = 0; i < pack.content.quiz_questions; i++) {
      content.quiz_questions.push({
        id: `${packId}_q_${i + 1}`,
        question: `Sample question ${i + 1} for ${pack.name}`,
        options: ['Option A', 'Option B', 'Option C', 'Option D'],
        correct_answer: Math.floor(Math.random() * 4),
        difficulty: ['easy', 'medium', 'hard'][Math.floor(Math.random() * 3)],
        explanation: `This is the explanation for question ${i + 1}`,
        biome: packId,
        offline: true
      });
    }

    for (let i = 0; i < pack.content.articles; i++) {
      content.articles.push({
        id: `${packId}_article_${i + 1}`,
        title: `Article ${i + 1}: ${pack.name}`,
        content: `This is offline educational content about ${pack.name}...`,
        reading_time: Math.ceil(Math.random() * 5),
        images: Math.floor(Math.random() * 3),
        offline: true
      });
    }

    for (let i = 0; i < pack.content.images; i++) {
      content.images.push({
        id: `${packId}_img_${i + 1}`,
        url: `offline://images/${packId}/${i + 1}.jpg`,
        caption: `Image ${i + 1} from ${pack.name}`,
        cached: true,
        offline: true
      });
    }

    return content;
  }

  /**
   * Cache content in IndexedDB
   */
  async cacheContent(packId, content, version) {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('LandGridOfflineContent', 1);

      request.onerror = () => reject(request.error);

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains('contentPacks')) {
          db.createObjectStore('contentPacks', { keyPath: 'id' });
        }
      };

      request.onsuccess = (event) => {
        const db = event.target.result;
        const transaction = db.transaction(['contentPacks'], 'readwrite');
        const store = transaction.objectStore('contentPacks');

        const estimatedSize = (content?.metadata?.size) || (JSON.stringify(content).length / (1024 * 1024));

        const data = {
          id: packId,
          content,
          version,
          cached_at: new Date().toISOString(),
          size: estimatedSize
        };

        const putRequest = store.put(data);
        putRequest.onsuccess = () => resolve(data);
        putRequest.onerror = () => reject(putRequest.error);
      };
    });
  }

  /**
   * Get cached content
   */
  async getCachedContent(packId) {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('LandGridOfflineContent', 1);

      request.onerror = () => reject(request.error);

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains('contentPacks')) {
          db.createObjectStore('contentPacks', { keyPath: 'id' });
        }
      };

      request.onsuccess = (event) => {
        const db = event.target.result;

        // Check if object store exists
        if (!db.objectStoreNames.contains('contentPacks')) {
          resolve(null);
          return;
        }

        const transaction = db.transaction(['contentPacks'], 'readonly');
        const store = transaction.objectStore('contentPacks');

        const getRequest = store.get(packId);
        getRequest.onsuccess = () => resolve(getRequest.result);
        getRequest.onerror = () => reject(getRequest.error);
      };
    });
  }

  /**
   * Load cached content info
   */
  async loadCachedContentInfo() {
    try {
      const request = indexedDB.open('LandGridOfflineContent', 1);

      return new Promise((resolve) => {
        request.onupgradeneeded = (event) => {
          const db = event.target.result;
          if (!db.objectStoreNames.contains('contentPacks')) {
            db.createObjectStore('contentPacks', { keyPath: 'id' });
          }
        };

        request.onsuccess = (event) => {
          const db = event.target.result;

          // Check if object store exists
          if (!db.objectStoreNames.contains('contentPacks')) {
            resolve([]);
            return;
          }

          const transaction = db.transaction(['contentPacks'], 'readonly');
          const store = transaction.objectStore('contentPacks');
          const getAllRequest = store.getAll();

          getAllRequest.onsuccess = () => {
            const cachedPacks = getAllRequest.result || [];
            cachedPacks.forEach(cached => {
              const pack = this.contentPacks.get(cached.id);
              if (pack) {
                pack.cached = true;
                pack.cached_at = cached.cached_at;
                pack.cached_version = cached.version;
                pack.cached_size = cached.size;
              }
            });
            resolve(cachedPacks);
          };
        };

        request.onerror = () => resolve([]);
      });
    } catch (error) {
      console.warn('Could not load cached content info:', error);
      return [];
    }
  }

  /**
   * Remove cached content pack
   */
  async removeCachedContent(packId) {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('LandGridOfflineContent', 1);

      request.onerror = () => reject(request.error);

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains('contentPacks')) {
          db.createObjectStore('contentPacks', { keyPath: 'id' });
        }
      };

      request.onsuccess = (event) => {
        const db = event.target.result;

        // Check if object store exists
        if (!db.objectStoreNames.contains('contentPacks')) {
          resolve();
          return;
        }

        const transaction = db.transaction(['contentPacks'], 'readwrite');
        const store = transaction.objectStore('contentPacks');

        const deleteRequest = store.delete(packId);
        deleteRequest.onsuccess = () => {
          // Update pack metadata
          const pack = this.contentPacks.get(packId);
          if (pack) {
            delete pack.cached;
            delete pack.cached_at;
            delete pack.cached_version;
            delete pack.cached_size;
          }
          resolve();
        };
        deleteRequest.onerror = () => reject(deleteRequest.error);
      };
    });
  }

  /**
   * Get offline content for biome
   */
  async getOfflineContent(packId, contentType = null) {
    const cached = await this.getCachedContent(packId);
    if (!cached) {
      return null;
    }

    if (contentType) {
      return cached.content[contentType] || [];
    }

    return cached.content;
  }

  /**
   * Check if content pack is available offline
   */
  async isAvailableOffline(packId) {
    const cached = await this.getCachedContent(packId);
    const pack = this.contentPacks.get(packId);

    return cached && pack && cached.version === pack.version;
  }

  /**
   * Get download recommendations
   */
  getDownloadRecommendations(userPreferences = {}) {
    const recommendations = [];
    const maxTotalSize = userPreferences.max_total_size || 10; // MB
    let currentSize = 0;

    // Always recommend essential pack first
    const essential = this.contentPacks.get('essential');
    if (essential && !essential.cached) {
      recommendations.push({
        pack: essential,
        reason: 'Essential content for offline learning',
        priority: 1
      });
      currentSize += essential.size;
    }

    // Recommend based on user preferences
    const availablePacks = this.getAvailableContentPacks(userPreferences);

    for (const pack of availablePacks) {
      if (pack.id === 'essential' || pack.cached) continue;
      if (currentSize + pack.size > maxTotalSize) break;

      let reason = '';
      let priority = 2;

      if (pack.offline_priority === 'high') {
        reason = 'High priority educational content';
        priority = 2;
      } else if (userPreferences.favorite_biomes?.includes(pack.id)) {
        reason = 'Matches your favorite biomes';
        priority = 1;
      } else {
        reason = 'Additional educational content';
        priority = 3;
      }

      recommendations.push({ pack, reason, priority });
      currentSize += pack.size;
    }

    return recommendations.sort((a, b) => a.priority - b.priority);
  }

  /**
   * Auto-download essential content
   */
  async autoDownloadEssentials() {
    const essential = this.contentPacks.get('essential');
    if (essential && essential.auto_download && !essential.cached && this.isOnline) {
      try {
        await this.downloadContentPack('essential');
        if (typeof process === 'undefined' || process.env?.NODE_ENV !== 'test') {
          console.log('�o. Essential content pack downloaded automatically');
        }
      } catch (error) {
        console.warn('Failed to auto-download essential content:', error);
      }
    }
  }

  /**
   * Setup network status listeners
   */
  setupNetworkListeners() {
    window.addEventListener('online', () => {
      this.isOnline = true;
      console.log('Network connection restored - offline content sync available');
      this.onNetworkStatusChange?.(true);
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
      console.log('Network connection lost - using offline content only');
      this.onNetworkStatusChange?.(false);
    });
  }

  /**
   * Generate offline content report
   */
  generateOfflineReport() {
    const cachedPacks = Array.from(this.contentPacks.values()).filter(pack => pack.cached);
    const totalCachedSize = cachedPacks.reduce((sum, pack) => sum + (pack.cached_size || 0), 0);

    return {
      online_status: this.isOnline,
      storage: this.storage,
      total_content_packs: this.contentPacks.size,
      cached_packs: cachedPacks.length,
      cached_size_mb: Math.round(totalCachedSize * 100) / 100,
      download_queue: this.downloadQueue.length,
      cached_content: cachedPacks.map(pack => ({
        id: pack.id,
        name: pack.name,
        size_mb: pack.cached_size || 0,
        cached_at: pack.cached_at,
        version: pack.cached_version
      })),
      recommendations: this.getDownloadRecommendations()
    };
  }

  /**
   * Initialize offline content system
   */
  async initialize() {
    await this.initializeOfflineContent();

    // Auto-download essentials if online
    if (this.isOnline) {
      setTimeout(() => this.autoDownloadEssentials(), 2000);
    }

    const shouldLog = typeof process === 'undefined' || process.env?.NODE_ENV !== 'test';

    if (shouldLog) {
      console.log('�o. Offline Content Manager initialized');
      console.log('dY"� Available content packs:', this.contentPacks.size);
    }

    const report = this.generateOfflineReport();
    if (report.cached_packs > 0 && shouldLog) {
      console.log(`dY'_ ${report.cached_packs} packs cached (${report.cached_size_mb}MB)`);
    }
  }
}


